import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Grid } from "antd";
import { AppContext } from "../context/AppContext";
import { getMenuSections } from "./menuFilters";
import "./topNav.css";

// Build a sub-item URL the same way subMenu.jsx does: append the status
// segment unless it's the "0" sentinel.
const buildSubUrl = ({ subLink, status }) =>
  status && status !== "0" ? `${subLink}/${status}` : subLink;

// A single top-level entry: either a plain link or a dropdown trigger.
const TopNavEntry = ({ item, openKey, setOpenKey, onNavigate }) => {
  const hasSub = item.type === "treeview" && item.sub_menus?.length > 0;

  if (!hasSub) {
    return (
      <li className="top-nav-item">
        <Link className="top-nav-link" to={item.link} onClick={onNavigate}>
          {item.icon && <i className={item.icon} />}
          <span>{item.text}</span>
        </Link>
      </li>
    );
  }

  const isOpen = openKey === item.id;
  return (
    <li className={`top-nav-item ${isOpen ? "open" : ""}`}>
      <button
        type="button"
        className="top-nav-trigger"
        onClick={() => setOpenKey(isOpen ? null : item.id)}
      >
        {item.icon && <i className={item.icon} />}
        <span>{item.text}</span>
        <i className="fa fa-angle-down top-nav-chevron" />
      </button>
      <ul className="top-nav-dropdown">
        {item.sub_menus.map((sub, index) => (
          <li key={index}>
            <Link to={buildSubUrl(sub)} onClick={onNavigate}>
              <i className="fa fa-hand-o-right" />
              {sub.name}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
};

const TopNav = () => {
  const { user, menus, company } = useContext(AppContext);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Which dropdown is open (click-toggled); CSS :hover also opens on desktop.
  const [openKey, setOpenKey] = useState(null);
  const navRef = useRef(null);

  const { mainMenuItems, reportMenuItems } = getMenuSections(
    menus,
    user,
    company,
  );

  // Reports section is admin-only, matching the sidebar.
  const items = [
    ...mainMenuItems,
    ...(user?.role === "Administrator" ? reportMenuItems : []),
  ];

  // Close any open dropdown when clicking outside the nav.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenKey(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeMenus = () => setOpenKey(null);

  // On mobile, collapse the whole menu into a single "Menu" dropdown so the
  // bar doesn't overflow horizontally.
  if (isMobile) {
    const isOpen = openKey === "__mobile__";
    return (
      <nav className="top-nav top-nav-mobile" ref={navRef}>
        <ul className="top-nav-item-list" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex" }}>
          <li className={`top-nav-item ${isOpen ? "open" : ""}`}>
            <button
              type="button"
              className="top-nav-trigger"
              onClick={() => setOpenKey(isOpen ? null : "__mobile__")}
            >
              <i className="fa fa-bars" />
              <span>Menu</span>
              <i className="fa fa-angle-down top-nav-chevron" />
            </button>
            <ul className="top-nav-dropdown">
              {items.map((item) => {
                const hasSub =
                  item.type === "treeview" && item.sub_menus?.length > 0;
                if (!hasSub) {
                  return (
                    <li key={item.id}>
                      <Link to={item.link} onClick={closeMenus}>
                        {item.icon && <i className={item.icon} />}
                        {item.text}
                      </Link>
                    </li>
                  );
                }
                // Flatten submenu items inline under the mobile list.
                return item.sub_menus.map((sub, index) => (
                  <li key={`${item.id}-${index}`}>
                    <Link to={buildSubUrl(sub)} onClick={closeMenus}>
                      <i className="fa fa-hand-o-right" />
                      {item.text} · {sub.name}
                    </Link>
                  </li>
                ));
              })}
            </ul>
          </li>
        </ul>
      </nav>
    );
  }

  return (
    <nav className="top-nav" ref={navRef}>
      <ul
        className="top-nav-item-list"
        style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap" }}
      >
        {items.map((item) => (
          <TopNavEntry
            key={item.id}
            item={item}
            openKey={openKey}
            setOpenKey={setOpenKey}
            onNavigate={closeMenus}
          />
        ))}
      </ul>
    </nav>
  );
};

export default TopNav;
